import { AfterViewInit, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { StateService } from '@uirouter/core';
import { Subscription } from 'rxjs';

import { CimaCore, Cloudinary, Formatter, SessionStorage, Util, XmStore, XmStoreUtil } from 'core/services';
import { UsageDetail } from 'store/usage/models';
import { ActivityOptions, LogDirection, MessageType, USAGE_SUMMARY_STATE } from 'my-account/constants';
import { StoreAction } from 'store/actions';
import { CmsCountryCodingMapping, CmsUsageDetail } from 'store/cms/models';
import { GtpSession, GtpSessionCollection } from 'store/statement/models';
import { AccountSummary, LineSummary } from 'store/account-summary/models';
import { IXMOptions } from 'core/interfaces';
import { CONFIG_TOKEN } from 'core/constants';
import { User } from 'store/user/models';

@Component({
    selector: 'xm-usage-detail',
    styleUrls: ['./usage-detail.scss'],
    templateUrl: './usage-detail.html',
    standalone: false
})

export class XmUsageDetailComponent implements AfterViewInit, OnInit, OnDestroy {
    public activityOptions: DropdownItem[] = [];
    public selectedActivity: string;
    public usage: UsageDetail;
    public cycleDate: string;
    public activities: UsageDetailView[];
    public lineNumber: string;
    public nickName: string;
    public pageData: CmsUsageDetail;
    public apiRequestData: StoreFetchInfo;
    public roamingDataConsumed: string;
    public gtpSessions: GtpSession[];
    public cmsCountryCode: CountryCode;
    public lineSummary: LineSummary;
    public user: User;

    private state: StateService;
    private subscriptions: Subscription[] = [];
    private xmStore: XmStore;
    private sessionStorage: SessionStorage;
    private statementLineId: string;
    private config: IXMOptions;
    private cimaCore: CimaCore;

    constructor(sessionStorage: SessionStorage, state: StateService, xmStore: XmStore, @Inject(CONFIG_TOKEN) config: IXMOptions, cimaCore: CimaCore) {
        Object.assign(this, { sessionStorage, state, xmStore, config, cimaCore });
    }

    public ngOnInit(): void {
        this.selectedActivity = this.state.params.activity;
        this.lineNumber = this.state.params.lineNumber;
        this.statementLineId = this.state.params.statement_line_id;

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<User>(User), (user: User) => {
            this.user = user;
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsUsageDetail>(CmsUsageDetail), (result: CmsUsageDetail) => {
            this.pageData = result;
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsCountryCodingMapping>(CmsCountryCodingMapping), (result: CmsCountryCodingMapping) => {
            this.cmsCountryCode = result.gtpCountryCodes;
        }));

        XmStoreUtil.defaultCatch(this.xmStore.findPromise<UsageDetail>(StoreAction.GET_USAGE_DETAIL, UsageDetail,
            {
                filters: { 'statementLineId.key': this.statementLineId },
                params: { statementLineId: this.statementLineId, cycleStartDate: this.state.params.startDate, cycleEndDate: this.state.params.endDate }
            }
        ).then((usageDetail: UsageDetail) => {
            this.usage = usageDetail;
            // set dropdown options
            this.initializeDropDown();
            this.cycleDate = Formatter.formatCycleDuration(this.usage.cycleStartDate, this.usage.cycleEndDate);
            this.nickName = this.pageData.header.replace('[NAME]', this.state.params.name);
            this.switchActivities();

            return;
        }).catch(() => {
            this.state.go('error');
        }));
    }

    public ngAfterViewInit(): void {
        // not to call Account summary if regular message and call filter only
        const isRegCallOrMessages: boolean = this.activityOptions.every((option: DropdownItem) => (option.value === ActivityOptions.MESSAGES || option.value === ActivityOptions.CALLS));

        if (!isRegCallOrMessages) {
            this.callAccountSummary();
        }
    }

    public changeActivity(dropdownOutput: DropdownOutput): void {
        this.selectedActivity = dropdownOutput.selection.value;
        this.state.go(this.state.current.name, { ...this.state.params, activity: this.selectedActivity, statement_line_id: this.statementLineId });
        this.switchActivities();
    }

    public buildIcon(calls: UsageDetailView): MediaImageOptions {
        let icon: CmsMediaImage;

        if (calls.isPhoneCall) {
            icon = this.isIncomingDirection(calls) ? this.pageData.incomingCallIcon : this.pageData.outgoingCallIcon;
        } else if (calls.isMessage) {
            icon = this.isIncomingDirection(calls) ? this.pageData.incomingMessageIcon : this.pageData.outgoingMessageIcon;
        } else {
            icon = this.pageData.dataIcon;
        }

        return {
            mobile: Cloudinary.generateMediaFromCms(icon)
        };
    }

    public formatTime(timestamp: string): string {
        return new Date(timestamp).toLocaleTimeString('en-EN', {
            timeZoneName: 'short'
        });
    }

    public formatGtpPassStartEndTime(timestamp: string): string {
        return new Date(timestamp).toLocaleTimeString('en-EN', {
            hour: '2-digit',
            minute: '2-digit',
            timeZoneName: 'short'
        });
    }

    public searchCountryCta(): void {
        this.sessionStorage.set(USAGE_SUMMARY_STATE, 'account.billing.landing');

        (this.isCallsToNonUSActivity || this.isMessagesToNonUsActivity || this.isCallsToMexAndCan) ?
            this.state.go('account.activity.details.international-calling', { line_id: this.lineSummary.id.key }) : this.state.go('account.activity.details.travel-rate', { line_id: this.lineSummary.id.key });
    }

    public get isCallsActivity(): boolean {
        return this.selectedActivity === ActivityOptions.CALLS;
    }

    public get isCallsToNonUSActivity(): boolean {
        return this.selectedActivity === ActivityOptions.CALLS_TO_NON_US_NUMBER;
    }

    public get isCallsWhileOutofUSActivity(): boolean {
        return this.selectedActivity === ActivityOptions.CALLS_WHILE_OUTSIDE_US;
    }

    public get isMessagesActivity(): boolean {
        return this.selectedActivity === ActivityOptions.MESSAGES;
    }

    public get isMessagesToNonUsActivity(): boolean {
        return this.selectedActivity === ActivityOptions.MESSAGES_TO_NON_US_NUMBER;
    }

    public get isMessagesWhileOutofUSActivity(): boolean {
        return this.selectedActivity === ActivityOptions.MESSAGES_WHILE_OUTSIDE_US;
    }

    public get isDataWhileOutofUSActivity(): boolean {
        return this.selectedActivity === ActivityOptions.DATA_WHILE_OUTSIDE_US;
    }

    public isIncomingDirection(activity: UsageDetailView): boolean {
        return activity.logDirection === LogDirection.INCOMING;
    }

    public isOutgoingDirection(activity: UsageDetailView): boolean {
        return activity.logDirection === LogDirection.OUTGOING;
    }

    public get isCallsToMexAndCan(): boolean {
        return this.selectedActivity === ActivityOptions.MEX_CAN_CALLS;
    }

    public get isGlobalTravelPass(): boolean {
        return this.selectedActivity === ActivityOptions.GLOBAL_TRAVEL_PASS;
    }

    public get isMexCanTravelPass(): boolean {
        return this.selectedActivity === ActivityOptions.MEX_CAN_TRAVEL_PASS;
    }

    public formatGtpPassStartDate(startDate: string): string {
        return new Date(startDate).toLocaleDateString('en-EN', {
            month: 'short',
            day: 'numeric'
        });
    }

    public ngOnDestroy(): void {
        Util.unsubscribeAll(this.subscriptions);
    }

    private callAccountSummary(): void {
        let action: string;

        if (this.config.ENABLE_CBM_GEMINI && this.cimaCore.isBusinessUser && this.user.account.isNoLevel) {
            action = StoreAction.GET_SSM_ACCOUNT_SUMMARY_CBM_NO_LEVEL;
        } else if (this.user.account.isSegmentSix && !this.cimaCore.isBusinessUser) {
            action = StoreAction.GET_ACCOUNT_SUMMARY_FOR_PLAN_EIGHT;
        } else if (this.user.account.isSegmentFive || this.user.account.isSegmentSix) {
            action = StoreAction.GET_ACCOUNT_SUMMARY_VIA_SSM_PROXY;
        } else {
            action = StoreAction.GET_ACCOUNT_SUMMARY;
        } 

        this.xmStore.findPromise<AccountSummary>(action, AccountSummary).then((result: AccountSummary) => {
            this.lineSummary = result.getLineSummaryByLineNumber(this.state.params.lineNumber);
        });
    }

    private initializeDropDown(): void {
        if (this.usage.domesticVoiceCounts > 0) {
            this.activityOptions.push({
                label: 'Calls',
                value: ActivityOptions.CALLS,
                selected: this.state.params.activity === ActivityOptions.CALLS
            });
        }

        if (this.usage.internationalVoiceCounts > 0) {
            this.activityOptions.push({
                label: 'Calls to non-US numbers',
                value: ActivityOptions.CALLS_TO_NON_US_NUMBER,
                selected: this.state.params.activity === ActivityOptions.CALLS_TO_NON_US_NUMBER
            });
        }

        if (this.usage.roamingVoiceCounts > 0) {
            this.activityOptions.push({
                label: 'Calls while outside US',
                value: ActivityOptions.CALLS_WHILE_OUTSIDE_US,
                selected: this.state.params.activity === ActivityOptions.CALLS_WHILE_OUTSIDE_US
            });
        }

        if (this.usage.domesticMessageCounts > 0) {
            this.activityOptions.push({
                label: 'Messages',
                value: ActivityOptions.MESSAGES,
                selected: this.state.params.activity === ActivityOptions.MESSAGES
            });
        }

        if (this.usage.internationalMessageCounts > 0) {
            this.activityOptions.push({
                label: 'Messages to non-US numbers',
                value: ActivityOptions.MESSAGES_TO_NON_US_NUMBER,
                selected: this.state.params.activity === ActivityOptions.MESSAGES_TO_NON_US_NUMBER
            });
        }

        if (this.usage.roamingMessageCounts > 0) {
            this.activityOptions.push({
                label: 'Messages while outside US',
                value: ActivityOptions.MESSAGES_WHILE_OUTSIDE_US,
                selected: this.state.params.activity === ActivityOptions.MESSAGES_WHILE_OUTSIDE_US
            });
        }

        if (this.usage.roamingDataConsumed > 0) {
            this.roamingDataConsumed = Math.ceil(this.usage.roamingDataConsumed).toString();
            this.activityOptions.push({
                label: 'Data while outside US',
                value: ActivityOptions.DATA_WHILE_OUTSIDE_US,
                selected: this.state.params.activity === ActivityOptions.DATA_WHILE_OUTSIDE_US
            });
        }

        if (this.usage.mexCanCalls > 0) {
            this.activityOptions.push({
                label: 'Mexico and Canada Calls',
                value: ActivityOptions.MEX_CAN_CALLS,
                selected: this.state.params.activity === ActivityOptions.MEX_CAN_CALLS
            });
        }

        // Global Travel Pass for Mexico & Canada
        if (this.usage.canMexTravelPassUsage.voiceCounts > 0 || this.usage.canMexTravelPassUsage.textCounts > 0 || this.usage.canMexTravelPassUsage.dataLog.length) {
            this.activityOptions.push({
                label: 'Global Travel Pass (Mexico & Canada)',
                value: ActivityOptions.MEX_CAN_TRAVEL_PASS,
                selected: this.state.params.activity === ActivityOptions.MEX_CAN_TRAVEL_PASS
            });
        }

        // Global Travel Pass
        if (this.usage.globalTravelPassUsage.voiceCounts > 0 || this.usage.globalTravelPassUsage.textCounts > 0 || this.usage.globalTravelPassUsage.dataLog.length) {
            this.activityOptions.push({
                label: 'Global Travel Pass',
                value: ActivityOptions.GLOBAL_TRAVEL_PASS,
                selected: this.state.params.activity === ActivityOptions.GLOBAL_TRAVEL_PASS
            });
        }
    }

    private switchActivities(): void {
        // get relevant activities for view
        switch (this.selectedActivity) {
            case ActivityOptions.CALLS:
                this.activities = this.getActivitiesGroupByDate(this.usage.calls);
                break;
            case ActivityOptions.CALLS_TO_NON_US_NUMBER:
                this.activities = this.getActivitiesGroupByDate(this.usage.callsToNonUS);
                break;
            case ActivityOptions.CALLS_WHILE_OUTSIDE_US:
                this.activities = this.getActivitiesGroupByDate(this.usage.callsWhileOutsideUS);
                break;
            case ActivityOptions.MESSAGES:
                this.activities = this.getActivitiesGroupByDate(this.usage.messages);
                break;
            case ActivityOptions.MESSAGES_TO_NON_US_NUMBER:
                this.activities = this.getActivitiesGroupByDate(this.usage.messagesToNonUS);
                break;
            case ActivityOptions.MESSAGES_WHILE_OUTSIDE_US:
                this.activities = this.getActivitiesGroupByDate(this.usage.messagesWhileOutsideUS);
                break;
            case ActivityOptions.DATA_WHILE_OUTSIDE_US:
                this.activities = this.getActivitiesGroupByDate(this.usage.dataWhileOutsideUS);
                break;
            case ActivityOptions.MEX_CAN_CALLS:
                this.activities = this.getActivitiesGroupByDate(this.usage.callsToMexCan);
                break;
            case ActivityOptions.MEX_CAN_TRAVEL_PASS:
                this.activities = this.getActivitiesGroupByDate(this.usage.usageOfCanMexGlobalTravelPass);
                this.callGTPSessionApi();
                break;
            case ActivityOptions.GLOBAL_TRAVEL_PASS:
                this.activities = this.getActivitiesGroupByDate(this.usage.usageOfGlobalTravelPass);
                this.callGTPSessionApi();
                break;
        }
    }

    private callGTPSessionApi(): void {
        XmStoreUtil.defaultCatch(this.xmStore.findPromise<GtpSessionCollection>(StoreAction.GET_GTP_SESSION_COLLECTION, GtpSessionCollection, {
            params: { statement_id: this.state.params.statement_line_id },
            filters: { statementKey: this.state.params.statement_line_id }
        }).then((result: GtpSessionCollection) => {
            this.gtpSessions = this.selectedActivity === ActivityOptions.GLOBAL_TRAVEL_PASS ? result.restOfCountyGTPPasses : result.canMexGtpPasses;
        }));
    }

    private getActivitiesGroupByDate(usageLogs: UsageLog[]): UsageDetailView[] {
        // format UsageLog[] to html required format

        const transformedArray: UsageDetailView[] = usageLogs.map<UsageDetailView>((call: UsageLog) => {
            const transformed: UsageDetailView = {};

            transformed.isPhoneCall = Boolean(call.callDuration);
            transformed.isMessage = Boolean(call.type);
            transformed.isDataLog = Boolean(call.dataUsed);
            transformed.phone = call.remoteNumber;
            transformed.location = call.remoteLocation || call.location; // this is weired but have to do because of API name inconsistency
            transformed.cost = call.cost;
            transformed.duration = call.callDuration;
            transformed.messageType = call.type === MessageType.TEXT ? 'text' : 'Multimedia';
            transformed.dataUsed = call.dataUsed;
            transformed.logDirection = call.direction;
            transformed.timestamp = call.timestamp;
            transformed.time = this.formatTime(call.timestamp);

            transformed.date = new Date(call.timestamp).toLocaleDateString('en-EN', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });


            return transformed;
        });

        // sort in decending order dates for GTP as GTP has all three types of usage togather (Voice Text and Data)
        transformedArray.sort((usageDetailItem1: UsageDetailView, usageDetailItem2: UsageDetailView) => +new Date(usageDetailItem2.timestamp).getTime() - +new Date(usageDetailItem1.timestamp).getTime());

        // return group by transformed array on date field here;
        return transformedArray.reduce((result: UsageDetailView[], usageDetail: UsageDetailView) => {
            result[usageDetail.date] = result[usageDetail.date] || [];
            result[usageDetail.date].push(usageDetail);

            return result;
        }, []);
    }
}
